import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleById, getRecentArticles } from '@/mocks/articles';
import { getCategoryBySlug } from '@/mocks/categories';
import type { Article } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | undefined>();
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (!slug) return;

    const found = getArticleById(slug);
    setArticle(found);

    if (found) {
      const related = found.relatedArticles
        .map((id) => getArticleById(id))
        .filter((a): a is Article => !!a);
      setRelatedArticles(related);
    }
  }, [slug]);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 韩漫免费阅读`;
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <p className="mt-4 text-foreground-500">文章未找到</p>
            <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 text-sm font-medium">返回首页</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const category = getCategoryBySlug(article.category);
  const recentArticles = getRecentArticles(3).filter(a => a.id !== article.id);

  const schemaArticle = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishDate,
    image: article.cover,
    url: `https://www.example.com/article/${article.id}`,
    publisher: {
      '@type': 'Organization',
      name: '韩漫免费阅读',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.example.com/article/${article.id}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <section className="bg-background-100 py-6">
            <div className="max-w-[960px] mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
                  { label: article.title },
                ]}
              />
            </div>
          </section>

          <article className="max-w-[960px] mx-auto px-4 md:px-6 py-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 mt-4 pb-6 border-b border-background-200/70">
              <span className="text-sm text-foreground-500">{article.author}</span>
              <span className="text-sm text-foreground-400">{article.publishDate}</span>
              <span className="text-sm text-foreground-400">{article.readTime}阅读</span>
              <span className="text-sm text-foreground-400">{article.views} 次浏览</span>
              <div className="flex items-center gap-1.5">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 rounded-md bg-accent-100 text-accent-800 text-xs font-medium">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 韩漫免费阅读`}
                className="w-full rounded-xl object-cover aspect-[16/9]"
              />
            </div>

            <div
              className="mt-8 prose prose-sm max-w-none text-foreground-800 leading-relaxed
                [&_h2]:font-heading [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
                [&_h3]:font-heading [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-6 [&_h3]:mb-3
                [&_p]:mb-4 [&_p]:leading-relaxed
                [&_ul]:mb-4 [&_ul]:pl-5 [&_ul]:space-y-2 [&_ul]:list-disc
                [&_ol]:mb-4 [&_ol]:pl-5 [&_ol]:space-y-2 [&_ol]:list-decimal
                [&_li]:leading-relaxed [&_strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  {category && (
                    <Link
                      to={`/category/${category.slug}`}
                      className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-arrow-left-line"></i> 返回{category.name}
                    </Link>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-foreground-400">分享：</span>
                  <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors" aria-label="分享到微博">
                    <i className="ri-weibo-line"></i>
                  </a>
                  <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors" aria-label="分享到微信">
                    <i className="ri-wechat-line"></i>
                  </a>
                  <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors" aria-label="复制链接">
                    <i className="ri-link"></i>
                  </a>
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="max-w-[960px] mx-auto px-4 md:px-6 pb-8">
              <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">相关推荐</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} article={ra} variant="horizontal" />
                ))}
              </div>
            </section>
          )}

          <section className="max-w-[960px] mx-auto px-4 md:px-6 pb-14">
            <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">最新文章</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {recentArticles.map((ra) => (
                <ArticleCard key={ra.id} article={ra} />
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaArticle) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.example.com/' },
              ...(category ? [{ '@type': 'ListItem', position: 2, name: category.name, item: `https://www.example.com/category/${category.slug}` }] : []),
              { '@type': 'ListItem', position: category ? 3 : 2, name: article.title },
            ],
          }),
        }}
      />
    </>
  );
}